import { createLazyResource, createMutation } from '@magicdoorinc/ui';
import { createSignal, createMemo } from 'solid-js';
import { createStore } from 'solid-js/store';
import { createMagicDoorContext } from '~/contexts/utils';
import { leasingRentalApplicationRepo } from '~/repositories/leasing/rentalApplicationRepository';
import { rentalApplicationRepo, RentalApplicationRepository } from '~/repositories/rentalApplicationRepository';
import { ApplicationDecision } from '~/swagger/leasing/Api';
import { pick } from '~/utils/tool';
import type * as Leasing from '~/swagger/leasing/Api';
import type {
  CreateRentalApplicationDto,
  RentalApplicationDto,
  RentalApplicationListDto,
  SetDecisionDto as UpdateRentalApplicationDecisionDto,
} from '~/swagger/leasing/Api';
import type { GetRentalApplicationsParams, IRentalApplicationDto } from '~/types/generalQuestions';
import type { currentUpdateType } from '~/utils/constant';

interface Pagination {
  current: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export type applicationStore = {
  applications: IRentalApplicationDto[];
  loading: boolean;
  application?: IRentalApplicationDto;
  selectedPotentialUnitIds: Map<string, boolean>;
  addPotentialUnit: boolean;
  updateLoading: boolean;
  currentUpdateType?: currentUpdateType | null;
};

const repo = new RentalApplicationRepository();

export const [RentalApplicationProvider, useRentalApplication] = createMagicDoorContext('RentalApplication', () => {
  const [rentalApplications, setRentalApplications] = createSignal<RentalApplicationListDto[]>([]);
  const [isLoading, setIsLoading] = createSignal<boolean>(true);
  const [error, setError] = createSignal<Error | undefined>(undefined);
  const [settings, settingsActions] = createLazyResource(() => rentalApplicationRepo.getApplicationSettings());

  const [pagination, setPagination] = createSignal<Pagination>({
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const [applicationStore, setApplicationStore] = createStore<applicationStore>({
    applications: [],
    loading: false,
    selectedPotentialUnitIds: new Map(),
    addPotentialUnit: false,
    updateLoading: false,
  });

  const onTogglePotentialUnitIdsSelect = (id?: string) => {
    if (!id) {
      return;
    }
    setApplicationStore('selectedPotentialUnitIds', (selected) => {
      if (selected.has(id)) {
        selected.delete(id);
      } else {
        selected.set(id, true);
      }
      return new Map(selected);
    });
  };

  const onAddPotentialUnit = () => {
    setApplicationStore('addPotentialUnit', true);
  };

  const fetchApplication = createMutation(async (applicationId = applicationStore.application?.id) => {
    if (!applicationId) {
      return;
    }
    const response = await repo.getApplication(applicationId);
    setApplicationStore({
      application: response,
    });
  });

  const fetchPaymentStatus = createMutation(async (applicationId = applicationStore.application?.id) => {
    if (!applicationId) {
      return;
    }
    try {
      const response = await repo.getApplication(applicationId);
      setApplicationStore('application', (prev) => ({
        ...prev!,
        paymentStatus: response.paymentStatus,
        rentalApplicationTransactionDto: response.rentalApplicationTransactionDto,
        refundAmount: response.refundAmount,
      }));
    } catch (error) {
      console.error('Failed to fetch payment status:', error);
    }
  });

  const baseUpdate = async (delta: Partial<CreateRentalApplicationDto>) => {
    const _delta = { ...delta };
    try {
      setApplicationStore('updateLoading', true);

      const payload = pick(applicationStore.application as RentalApplicationDto & Record<string, unknown>, [
        'firstName',
        'lastName',
        'email',
        'phone',
        'ssn',
        'dateOfBirth',
        'applyingWith',
        'desiredMoveInDate',
        'maritalStatus',
        'driversLicense',
        'incomeInformation',
        'pets',
        'cars',
        'residentialHistory',
        'emergencyContact',
        'employment',
        'applicationQuestions',
        'draft',
      ]) as any;

      if (payload.applicationQuestions instanceof Map) {
        payload.applicationQuestions = Object.fromEntries(payload.applicationQuestions);
      }

      await repo.updateApplication(applicationStore.application?.id as string, {
        ...payload,
        tenantId: applicationStore.application?.tenant?.id,
        interestedUnitIds: applicationStore.application?.interests?.map((interest) => interest.unit?.id) as string[],
        decision: applicationStore.application?.applicationDecision,
        ..._delta,
      });
      fetchApplication();
      setApplicationStore('selectedPotentialUnitIds', new Map());
    } catch (err: any) {
      throw new Error(err);
    } finally {
      setApplicationStore('updateLoading', false);
    }
  };

  const updatePotentialUnits = async (
    type: 'add' | 'remove',
    deltaIds: string[] = Array.from(applicationStore.selectedPotentialUnitIds.keys())
  ) => {
    try {
      const currentIds = applicationStore.application?.interests?.map((interest) => interest.unit?.id) as string[];
      const nextIds = type === 'add' ? [...currentIds, ...deltaIds] : currentIds.filter((id) => !deltaIds.includes(id));
      await baseUpdate({
        interestedUnitIds: nextIds,
      });
    } catch (error: any) {
      throw new Error(error);
    }
  };

  const getRentalApplications = async (
    params?: GetRentalApplicationsParams & {
      sort?: string;
      stage?: string;
      stages?: string[];
      needs?: string;
      screeningVerified?: boolean;
      excludeRetracted?: boolean;
      portfolioIds?: string[];
      propertyIds?: string[];
      unitIds?: string[];
    }
  ) => {
    setIsLoading(true);
    try {
      const { stages, decisions } = resolveLeasingListFilter(params);
      const response = await leasingRentalApplicationRepo.list({
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 50,
        sort: params?.sort,
        stage: params?.stage,
        stages,
        decisions,
        needs: params?.needs,
        newOnly: params?.newOnly,
        screeningVerified: params?.screeningVerified,
        excludeRetracted: params?.excludeRetracted,
        portfolioIds: params?.portfolioIds,
        propertyIds: params?.propertyIds,
        unitIds: params?.unitIds,
      });
      // Only `hasName` stays client-side — backend has no equivalent.
      const filtered = params?.hasName
        ? response.items.filter((i) => (i.firstName ?? '').trim() || (i.lastName ?? '').trim())
        : response.items;
      setRentalApplications(filtered.map(adaptLeasingListItem));
      setPagination({
        current: response.page,
        pageSize: response.pageSize,
        total: response.totalCount,
        totalPages: response.totalPages,
      });
    } catch (err: any) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateDecision = createMutation(async (rentalApplicationId: string, payload: UpdateRentalApplicationDecisionDto) => {
    const response = await leasingRentalApplicationRepo.setDecision(rentalApplicationId, payload);

    setRentalApplications((prev) =>
      prev.map((item) => ({
        ...item,
        applicationDecision: item.id === rentalApplicationId ? response.decision : item.applicationDecision,
      }))
    );
  });

  const tenantConvert = async (rentalApplicationId: string) => {
    try {
      return await repo.tenantConvert(rentalApplicationId);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  };

  const isInitial = createMemo(() => {
    return (
      !applicationStore.application?.applicationDecision ||
      [ApplicationDecision.Ignored, ApplicationDecision.NoDecision].includes(applicationStore.application?.applicationDecision)
    );
  });

  const isApplicationApproved = createMemo(() => applicationStore.application?.applicationDecision === ApplicationDecision.Approved);

  const canConvertToTenant = createMemo(() => isApplicationApproved() && !applicationStore.application?.tenant);

  const canConvertToLease = createMemo(() => isApplicationApproved() && !applicationStore.application?.lease);

  return {
    getRentalApplications,
    rentalApplications,
    isLoading,
    error,
    pagination,
    updateDecision,
    tenantConvert,
    applicationStore,
    fetchApplication,
    fetchPaymentStatus,
    onTogglePotentialUnitIdsSelect,
    onAddPotentialUnit,
    setApplicationStore,
    updatePotentialUnits,
    baseUpdate,
    get settings() {
      settingsActions.fetch();
      return settings;
    },
    isInitial,
    isApplicationApproved,
    canConvertToTenant,
    canConvertToLease,
  };
});

// --- Leasing-service adapters ---

const mapLeasingDecision = (decision?: string): ApplicationDecision => {
  switch (decision) {
    case 'approved':
      return ApplicationDecision.Approved;
    case 'rejected':
      return ApplicationDecision.Rejected;
    case 'ignored':
      return ApplicationDecision.Ignored;
    default:
      return ApplicationDecision.NoDecision;
  }
};

const adaptLeasingListItem = (item: Leasing.RentalApplicationListDto): RentalApplicationListDto =>
  ({
    id: item.id,
    firstName: item.firstName,
    lastName: item.lastName,
    email: item.email,
    phone: item.phone,
    hasBeenSubmitted: item.status === 'submitted',
    draft: item.status === 'draft',
    applicationDecision: mapLeasingDecision(item.decision),
    aiApplicationScore: item.aiApplicationScore,
    desiredMoveInDate: item.desiredMoveInDate,
    submittedAt: item.submittedAt,
    created: item.created,
    updated: item.updated,
    stage: item.stage,
    daysInStage: item.daysInStage,
    needs: item.needs,
    paymentStatus: item.paymentStatus,
  }) as unknown as RentalApplicationListDto;

const NON_DRAFT_STAGES = ['submitted', 'screened', 'decided'];

/**
 * Translate the legacy list-filter shape (draft/hasBeenSubmitted/new/decisions)
 * into the leasing list endpoint's `stages` + `decisions` params so the
 * backend does the filtering. Returns undefined when the caller already
 * passed `stages` directly, to avoid overriding an explicit choice.
 */
const resolveLeasingListFilter = (
  params?: GetRentalApplicationsParams & { stages?: string[] }
): { stages?: string[]; decisions?: Leasing.ApplicationDecision[] } => {
  if (!params) return {};

  const stages = (() => {
    if (params.stages) return params.stages;
    if (params.draft === true || params.hasBeenSubmitted === false) return ['draft'];
    if (params.draft === false || params.hasBeenSubmitted === true || params.new) return NON_DRAFT_STAGES;
    return undefined;
  })();

  const decisions = (() => {
    if (params.new) return ['noDecision'] as Leasing.ApplicationDecision[];
    if (params.decisions?.length) return params.decisions as unknown as Leasing.ApplicationDecision[];
    return undefined;
  })();

  return { stages, decisions };
};
